// src/components/dashboard/purchasetable.jsx
import { useMemo, useState, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  ClientSideRowModelModule,
  ModuleRegistry,
  NumberEditorModule,
  NumberFilterModule,
  PaginationModule,
  RowSelectionModule,
  TextEditorModule,
  TextFilterModule,
  ValidationModule,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css"; // Keep this for AG Grid functionality
import "ag-grid-community/styles/ag-theme-alpine.css"; // Keep this for AG Grid functionality
import { useDispatch, useSelector } from "react-redux";
import { GetDepositlist } from "../../redux/slices/depositlist";
import { useNavigate } from "react-router";
import moment from "moment";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import { paymentStatusConfig } from "../../lib/consts";

ModuleRegistry.registerModules([
  NumberEditorModule,
  TextEditorModule,
  TextFilterModule,
  NumberFilterModule,
  RowSelectionModule,
  PaginationModule,
  ClientSideRowModelModule,
  ValidationModule,
]);

function ListOfDeposits({ user }) {
  const transactions = useSelector((value) => value?.dplist?.data);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(GetDepositlist({ id: user }, navigate));
  }, [dispatch, user, navigate]);

  const [currentImageIndex, setCurrentImageIndex] = useState(-1);
  const [selectedImages, setSelectedImages] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [remarks, setRemarks] = useState([]);

  const handleImageClick = (images, index) => {
    setSelectedImages(images);
    setCurrentImageIndex(index);
  };

  const handleStatusClick = (params) => {
    setRemarks(params);
    setOpenModal(true);
  };

  const CustomTextFilter = (params) => {
    const [filterText, setFilterText] = useState("");

    const onFilterTextChange = (event) => {
      setFilterText(event.target.value);
      params.filterChangedCallback();
    };

    const doesFilterPass = (params) => {
      return params.data[params.colDef.field]
        .toString()
        .toLowerCase()
        .includes(filterText.toLowerCase());
    };

    const isFilterActive = () => {
      return filterText !== "";
    };

    return (
      <div className="flex flex-col">
        <input
          type="text"
          placeholder="Search..."
          value={filterText}
          onChange={onFilterTextChange}
          className="border border-gray-300 rounded p-2"
        />
      </div>
    );
  };

  const CustomNumberFilter = (params) => {
    const [filterValue, setFilterValue] = useState("");

    const onFilterValueChange = (event) => {
      setFilterValue(event.target.value);
      params.filterChangedCallback();
    };

    const doesFilterPass = (params) => {
      return params.data[params.colDef.field] >= filterValue;
    };

    const isFilterActive = () => {
      return filterValue !== "";
    };

    return (
      <div className="flex flex-col">
        <input
          type="number"
          placeholder="Min Value"
          value={filterValue}
          onChange={onFilterValueChange}
          className="border border-gray-300 rounded p-2"
        />
      </div>
    );
  };

  const [columnDefs] = useState([
    {
      field: "attachments",
      minWidth: 100,
      headerName: "Attachments",
      cellRenderer: (params) => {
        return params?.value?.length > 0 ? (
          <button
            className="ml-2 text-blue-500 underline"
            onClick={() => handleImageClick(params?.value, 0)}
          >
            View
          </button>
        ) : (
          <span className="text-gray-400">No Attachments</span>
        );
      },
    },
    {
      field: "balance",
      minWidth: 120,
      headerName: "Before Balance",
      filter: "agNumberColumnFilter",
      filterFramework: CustomNumberFilter, // Use custom number filter
      cellRenderer: (params) => `$${params.value}`,
    },
    {
      field: "amount",
      minWidth: 100,
      headerName: "Deposit Amount",
      filter: "agNumberColumnFilter",
      filterFramework: CustomNumberFilter, // Use custom number filter
      cellRenderer: (params) => `$${params.value}`,
    },
    {
      field: "balance",
      minWidth: 150,
      maxWidth: 150,
      headerName: "After Balance",
      cellRenderer: (params) => {
        const percent =
          (Number(params?.data.bonus) / 100) * Number(params?.data.amount);
        return `$${
          parseInt(params.value) + parseInt(params?.data?.amount) + percent
        }`;
      },
    },
    {
      field: "createdAt",
      minWidth: 150,
      headerName: "Date",
      filter: "agTextColumnFilter",
      filterFramework: CustomTextFilter, // Use custom text filter
      cellRenderer: (params) => moment(params.value).format("DD/MM/YY hh:mm A"),
    },
    {
      field: "status",
      minWidth: 120,
      headerName: "Status",
      cellRenderer: (params) => {
        const statusInfo = paymentStatusConfig[params.value];
        return (
          <div className="flex justify-start items-center capitalize gap-1">
            {statusInfo?.icon}
            <span
              className={`cursor-pointer ${
                params?.data?.remarks?.length > 0 &&
                params?.data?.remarks[0] !== "" &&
                params.value === "rejected"
                  ? "text-sky-500 underline"
                  : ""
              }`}
              onClick={() => {
                if (
                  params?.data?.remarks?.length > 0 &&
                  params?.data?.remarks[0] !== ""
                ) {
                  if (params.value === "rejected") {
                    handleStatusClick(params?.data);
                  }
                }
              }}
            >
              {params.value}
            </span>
          </div>
        );
      },
    },
  ]);

  const defaultColDef = useMemo(
    () => ({
      editable: false,
      filter: true,
      resizable: false,
      sortable: true,
      flex: 1,
      minWidth: 100,
    }),
    [],
  );

  return (
    <div className="grid-table">
      <div className="ag-theme-alpine custom-table">
        <AgGridReact
          rowData={transactions}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          pagination={true}
          paginationPageSize={40}
          domLayout="autoHeight"
          suppressMenuHide={true}
        />
      </div>
      {currentImageIndex >= 0 && (
        <Lightbox
          mainSrc={
            selectedImages[currentImageIndex]?.image ||
            selectedImages[currentImageIndex]
          }
          nextSrc={
            selectedImages[(currentImageIndex + 1) % selectedImages.length]
              ?.image
          }
          prevSrc={
            selectedImages[
              (currentImageIndex + selectedImages.length - 1) %
                selectedImages.length
            ]?.image
          }
          onCloseRequest={() => setCurrentImageIndex(-1)}
          onMovePrevRequest={() =>
            setCurrentImageIndex(
              (currentImageIndex + selectedImages.length - 1) %
                selectedImages.length,
            )
          }
          onMoveNextRequest={() =>
            setCurrentImageIndex(
              (currentImageIndex + 1) % selectedImages.length,
            )
          }
        />
      )}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box className="relative p-4 bg-white shadow-lg rounded-lg w-96 mx-auto mt-20 overflow-auto max-h-60">
          <button
            className="absolute top-2 right-2 text-gray-500 hover:text-black"
            onClick={() => setOpenModal(false)}
          >
            <CloseIcon />
          </button>
          <h3 className="text-xl text-gray-600 font-semibold">Remarks :</h3>
          <p className="text-gray-600 break-words">{remarks?.remarks}</p>
        </Box>
      </Modal>
    </div>
  );
}

export default ListOfDeposits;
